/** Onde pôr uma nuvem de pontos para ela coincidir com o modelo.
 *
 *  Dois defeitos, descobertos ao mesmo tempo com o levantamento do
 *  Snowdon Towers, e ambos a esconder a nuvem sem erro nenhum:
 *
 *  **1. As nuvens entravam deitadas.** Todos os formatos de levantamento
 *  (PTS, XYZ, LAS, E57, PLY) têm o Z para cima, como o IFC. O 3D tem o Y
 *  para cima. O leitor de IFC converte; o de nuvens não convertia, e
 *  todas as nuvens entravam de lado. Ninguém tinha dado por isso porque
 *  é preciso ter uma nuvem e um IFC do mesmo edifício ao mesmo tempo.
 *
 *  **2. A nuvem ficava a 425 km do edifício.** O IFC do Snowdon traz
 *  georreferenciação, e o viewer põe-no nas coordenadas reais da
 *  Pensilvânia, a x = 417 594 m. A nuvem fica nas coordenadas locais em
 *  que foi exportada, perto do zero. A câmara vai para o edifício e só
 *  vê até 1000 m: a nuvem estava carregada, mas fora de alcance.
 *
 *  O teste que diz que isto é a correcção certa, e não um palpite: dando
 *  à nuvem a mesma colocação que o viewer dá ao IFC, e pondo-a de pé, a
 *  caixa dela envolve a do edifício com uns metros à volta (o terreno do
 *  levantamento), e em altura só fica de fora a cave, que um laser de
 *  fora não vê. A nuvem foi exportada nas mesmas coordenadas do projecto.
 *
 *  Isso é verdade para este caso e para qualquer par exportado do mesmo
 *  projecto. **Não é verdade sempre**: uma nuvem feita por um topógrafo
 *  pode vir em coordenadas próprias. Por isso o alinhamento é um botão
 *  que se carrega, com a distância à vista, e não uma coisa que se faz
 *  às escondidas.
 *
 *  Ficheiro puro, sem three.js: matrizes como arrays de 16, por colunas,
 *  que é a forma que o three.js usa.
 */

export type Matriz16 = readonly number[]

export interface Caixa {
  min: readonly [number, number, number]
  max: readonly [number, number, number]
}

/** Rodar -90° à volta do X: (x, y, z) passa a (x, z, -y).
 *
 *  É a mesma conversão que o leitor de IFC faz, e tem de ser a mesma:
 *  uma nuvem e um IFC do mesmo projecto só coincidem se ambos forem
 *  convertidos da mesma maneira. */
export const Z_PARA_Y: Matriz16 = [
  1, 0, 0, 0,
  0, 0, -1, 0,
  0, 1, 0, 0,
  0, 0, 0, 1,
]

export const IDENTIDADE: Matriz16 = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
]

/** `a × b`, as duas por colunas. Aplicar o resultado a um ponto é
 *  aplicar primeiro `b` e depois `a`. */
export function multiplicar(a: Matriz16, b: Matriz16): number[] {
  const r = new Array<number>(16).fill(0)
  for (let col = 0; col < 4; col += 1) {
    for (let lin = 0; lin < 4; lin += 1) {
      let s = 0
      for (let k = 0; k < 4; k += 1) s += a[k * 4 + lin] * b[col * 4 + k]
      r[col * 4 + lin] = s
    }
  }
  return r
}

export function aplicar(
  m: Matriz16,
  p: readonly [number, number, number],
): [number, number, number] {
  const [x, y, z] = p
  return [
    m[0] * x + m[4] * y + m[8] * z + m[12],
    m[1] * x + m[5] * y + m[9] * z + m[13],
    m[2] * x + m[6] * y + m[10] * z + m[14],
  ]
}

/**
 * A matriz que põe a nuvem onde deve estar.
 *
 * Primeiro de pé (Z para Y), depois a colocação do modelo, se houver
 * modelo. Sem modelo fica só de pé, no sítio das suas coordenadas.
 */
export function matrizDaNuvem(matrizDoModelo: Matriz16 | null): number[] {
  if (!matrizDoModelo) return [...Z_PARA_Y]
  return multiplicar(matrizDoModelo, Z_PARA_Y)
}

/** A caixa de uma caixa depois de transformada: os oito cantos. */
export function transformarCaixa(m: Matriz16, c: Caixa): Caixa {
  const cantos: [number, number, number][] = []
  for (const x of [c.min[0], c.max[0]]) {
    for (const y of [c.min[1], c.max[1]]) {
      for (const z of [c.min[2], c.max[2]]) cantos.push(aplicar(m, [x, y, z]))
    }
  }
  const eixo = (k: 0 | 1 | 2, f: (...v: number[]) => number) =>
    f(...cantos.map((p) => p[k]))
  return {
    min: [eixo(0, Math.min), eixo(1, Math.min), eixo(2, Math.min)],
    max: [eixo(0, Math.max), eixo(1, Math.max), eixo(2, Math.max)],
  }
}

/** A folga entre duas caixas. Zero quando se tocam ou se sobrepõem. */
export function distanciaEntreCaixas(a: Caixa, b: Caixa): number {
  let soma = 0
  for (let k = 0; k < 3; k += 1) {
    const d = Math.max(0, a.min[k] - b.max[k], b.min[k] - a.max[k])
    soma += d * d
  }
  return Math.sqrt(soma)
}

/** A partir de quanto se avisa que a nuvem está longe do modelo.
 *
 *  Um quilómetro: nenhuma nuvem de um edifício está a um quilómetro do
 *  edifício de propósito, e abaixo disto uma diferença de coordenadas
 *  vê-se no ecrã e pode ser intencional. */
export const LONGE_METROS = 1000

export function estaLonge(nuvem: Caixa, modelo: Caixa): boolean {
  return distanciaEntreCaixas(nuvem, modelo) > LONGE_METROS
}

/** A distância dita como se diz. */
export function textoDaDistancia(metros: number): string {
  if (!Number.isFinite(metros)) return 'uma distância desconhecida'
  if (metros >= 1000) {
    const km = metros / 1000
    return `${(km >= 10 ? Math.round(km) : Math.round(km * 10) / 10).toString().replace('.', ',')} km`
  }
  return `${Math.round(metros)} m`
}
